/**
 * AST-based folding provider for LaTeX in Monaco Editor.
 *
 * Provides folding ranges for:
 *  • \begin{…} … \end{…} environments (nested correctly via AST)
 *  • Section commands (\section … next \section)
 *  • Consecutive comment blocks
 *  • Preamble (\documentclass … \begin{document})
 *
 * Usage:
 *   Call `registerLatexFoldingProvider(monaco)` once during setup.
 */

import type { languages } from "monaco-editor"
import { parseLatex, flattenEnvironments } from "./latex-parser"
import { LATEX_LANGUAGE_ID } from "./monaco-latex"

/**
 * Register the LaTeX folding range provider with Monaco.
 */
export function registerLatexFoldingProvider(
  monaco: typeof import("monaco-editor"),
): void {
  monaco.languages.registerFoldingRangeProvider(LATEX_LANGUAGE_ID, {
    provideFoldingRanges(model, _context, _token) {
      const source = model.getValue()
      const result = parseLatex(source)
      const ranges: languages.FoldingRange[] = []

      // ── 1. Environment folding ──
      const flatEnvs = flattenEnvironments(result.environments)
      for (const env of flatEnvs) {
        if (env.endLine > env.startLine) {
          ranges.push({
            start: env.startLine,
            end: env.endLine,
            kind: monaco.languages.FoldingRangeKind.Region,
          })
        }
      }

      // ── 2. Section folding ──
      for (const sec of result.sections) {
        if (sec.endLine > sec.startLine) {
          ranges.push({
            start: sec.startLine,
            end: sec.endLine,
            kind: monaco.languages.FoldingRangeKind.Region,
          })
        }
      }

      // ── 3. Consecutive comment block folding ──
      const lines = source.split("\n")
      let commentStart: number | null = null
      for (let i = 0; i < lines.length; i++) {
        const trimmed = lines[i].trimStart()
        const isComment = trimmed.startsWith("%")
        if (isComment) {
          if (commentStart === null) commentStart = i + 1 // 1-based
        } else {
          if (commentStart !== null && i + 1 - 1 > commentStart) {
            // At least 2 consecutive comment lines
            ranges.push({
              start: commentStart,
              end: i, // previous line (1-based)
              kind: monaco.languages.FoldingRangeKind.Comment,
            })
          }
          commentStart = null
        }
      }
      // Handle comment block at EOF
      if (commentStart !== null && lines.length >= commentStart + 1) {
        ranges.push({
          start: commentStart,
          end: lines.length,
          kind: monaco.languages.FoldingRangeKind.Comment,
        })
      }

      // ── 4. Preamble folding (\documentclass … \begin{document}) ──
      let preambleStart: number | null = null
      let preambleEnd: number | null = null
      for (let i = 0; i < lines.length; i++) {
        if (/\\documentclass/.test(lines[i])) preambleStart = i + 1
        if (/\\begin\{document\}/.test(lines[i])) {
          preambleEnd = i + 1
          break
        }
      }
      if (preambleStart !== null && preambleEnd !== null && preambleEnd > preambleStart) {
        ranges.push({
          start: preambleStart,
          end: preambleEnd - 1,
          kind: monaco.languages.FoldingRangeKind.Region,
        })
      }

      return ranges
    },
  })
}
